// ¿Está Pau mirando la oficina ahora mismo? Lo que decide si lo que hay en pantalla cuenta como visto (el punto blanco
// de los contratos con Santi). Solo si la pestaña se ve (`visibilityState === 'visible'`), tiene el foco
// (`document.hasFocus()`) y ha habido algo de Pau (ratón, teclado, dedo) en los últimos 2 minutos: una pestaña de fondo,
// otra ventana delante o el PC parado con la oficina abierta NO marcan nada. Al volver (la pestaña, el foco o el ratón),
// vuelve a ser true y quien lo usa marca entonces lo que tiene en pantalla.
import { useEffect, useState } from 'react';

/** Sin tocar nada durante esto, Pau ya no está mirando. */
export const INACTIVO_MS = 2 * 60_000;

const EVENTOS_DE_PAU = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart'] as const;

function seVe(): boolean {
  if (typeof document === 'undefined') return false;
  return document.visibilityState === 'visible' && document.hasFocus();
}

export function usePauMira(): boolean {
  const [visible, setVisible] = useState(seVe);
  const [activo, setActivo] = useState(true);

  useEffect(() => {
    let reloj: ReturnType<typeof setTimeout> | null = null;
    let ultimo = 0;
    const dormir = () => {
      if (reloj) clearTimeout(reloj);
      reloj = setTimeout(() => setActivo(false), INACTIVO_MS);
    };
    const actividad = () => {
      setActivo(true);
      // El ratón dispara muchos eventos: el reloj se rearma como mucho una vez por segundo.
      const ahora = Date.now();
      if (ahora - ultimo < 1000) return;
      ultimo = ahora;
      dormir();
    };
    // Volver a la pestaña (o traerla delante) es Pau volviendo: cuenta como actividad.
    const revisar = () => {
      const v = seVe();
      setVisible(v);
      if (v) {
        ultimo = 0;
        actividad();
      }
    };
    dormir();
    document.addEventListener('visibilitychange', revisar);
    window.addEventListener('focus', revisar);
    window.addEventListener('blur', revisar);
    for (const ev of EVENTOS_DE_PAU) window.addEventListener(ev, actividad, { passive: true, capture: true });
    revisar();
    return () => {
      if (reloj) clearTimeout(reloj);
      document.removeEventListener('visibilitychange', revisar);
      window.removeEventListener('focus', revisar);
      window.removeEventListener('blur', revisar);
      for (const ev of EVENTOS_DE_PAU) window.removeEventListener(ev, actividad, { capture: true });
    };
  }, []);

  return visible && activo;
}
